import { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  Collapsible,
  Icon,
} from '@shopify/polaris';
import { ChevronDownMinor, ChevronUpMinor } from '@shopify/polaris-icons';

interface OrderStatusWidgetProps {
  shop: string;
}

interface OrderStatusResponse {
  ok: boolean;
  orderNumber?: string;
  createdAt?: string;
  currentStatus?: {
    label: string;
    description?: string;
  };
  timeline?: Array<{
    offsetDays: number;
    label: string;
    description?: string;
    date: string;
  }>;
  nextMilestone?: {
    label: string;
    date: string;
  };
  orderDetails?: {
    lineItems: Array<{
      id: string;
      title: string;
      variantTitle?: string;
      quantity: number;
      price: string;
      image?: string;
    }>;
    discounts?: Array<{
      code: string;
      amount: string;
    }>;
    subtotal: string;
    shipping: string;
    total: string;
    currency: string;
  };
  code?: string;
  message?: string;
}

interface Settings {
  // Order Status Section
  section_title: string;
  section_subtitle?: string;
  section_bg_color?: string;
  section_text_color?: string;
  section_border_color?: string;
  helper_text: string;
  disclaimer_text: string;
  button_label: string;
  button_color?: string;
  order_status_enabled?: boolean | number;
  
  // FAQ Section
  faq_section_title?: string;
  faq_section_subtitle?: string;
  faq_bg_color?: string;
  faq_text_color?: string;
  faq_border_color?: string;
  faq_enabled?: boolean | number;
  
  // AI Section
  ai_section_title?: string;
  ai_section_subtitle?: string;
  ai_bg_color?: string;
  ai_text_color?: string;
  ai_enabled?: boolean | number;
}

export default function OrderStatusWidget({ shop }: OrderStatusWidgetProps) {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusData, setStatusData] = useState<OrderStatusResponse | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [faqs, setFaqs] = useState<Array<{ id: number; question: string; answer: string }>>([]);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  // Send height updates to parent window (for iframe synchronization)
  useEffect(() => {
    // Only run in iframe context
    if (window.parent === window) return;
    
    let lastSentHeight = 0;
    let isUpdating = false;
    let stableHeightCount = 0;
    let lastContentHeight = 0;
    
    const sendHeight = () => {
      // Prevent concurrent updates
      if (isUpdating) return;
      isUpdating = true;
      
      try {
        // Get the actual content height (not including any iframe expansion)
        const body = document.body;
        const html = document.documentElement;
        
        const contentHeight = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        
        // Check if content height is stable
        if (Math.abs(contentHeight - lastContentHeight) < 5) {
          stableHeightCount++;
        } else {
          stableHeightCount = 0;
        }
        lastContentHeight = contentHeight;
        
        // Stop sending updates if height has been stable for 3 checks
        if (stableHeightCount > 3 && lastSentHeight > 0) {
          console.log('✅ Height stabilized at', lastSentHeight + 'px - stopping updates');
          return;
        }
        
        // Add 70px buffer to ensure no scrollbar appears on the page
        const height = contentHeight + 70;
        
        // Use larger threshold (100px) to prevent small fluctuations from causing loops
        const heightDiff = Math.abs(height - lastSentHeight);
        if (heightDiff > 100 || lastSentHeight === 0) {
          console.log(`Height changed from ${lastSentHeight}px to ${height}px (diff: ${heightDiff}px, content: ${contentHeight}px)`);
          lastSentHeight = height;
          window.parent.postMessage({ type: 'iframe-height', height }, '*');
        } else {
          console.log(`Skipping update - change too small: ${heightDiff}px (threshold: 100px)`);
        }
      } finally {
        isUpdating = false;
      }
    };

    // Initial sends with increasing delays
    const initialTimers = [
      setTimeout(sendHeight, 100),
      setTimeout(sendHeight, 500),
      setTimeout(sendHeight, 1200),
    ];
    
    // Observe only specific changes that affect content height
    const observer = new MutationObserver((mutations) => {
      // Filter out style changes to prevent infinite loops
      const hasRelevantChange = mutations.some(mutation => {
        // Ignore if mutation is just style changes
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          return false;
        }
        // Ignore height/width related attributes
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'height' || mutation.attributeName === 'width')) {
          return false;
        }
        // Only care about structural changes
        return mutation.type === 'childList';
      });
      
      if (hasRelevantChange) {
        // Debounce: wait for changes to settle
        clearTimeout((window as any)._heightDebounce);
        (window as any)._heightDebounce = setTimeout(sendHeight, 500);
      }
    });
    
    // Observe only childList changes (ignore attributes completely)
    observer.observe(document.body, { 
      childList: true,
      subtree: true,
      // Do NOT watch attributes - they cause loops
    });

    // Handle window resize with debouncing
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(sendHeight, 500);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      initialTimers.forEach(timer => clearTimeout(timer));
      clearTimeout((window as any)._heightDebounce);
      clearTimeout(resizeTimeout);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [statusData, faqs, faqOpen]);

  // Get API base URL (for standalone widget mode)
  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined' && (window as any).__WIDGET_API_URL__) {
      const widgetUrl = (window as any).__WIDGET_API_URL__;
      console.log('[OrderStatusWidget] Using widget API URL:', widgetUrl);
      return widgetUrl;
    }
    // Fallback to current origin
    const origin = window.location.origin;
    console.log('[OrderStatusWidget] Using current origin as API URL:', origin);
    return origin;
  };

  const apiBaseUrl = getApiBaseUrl();
  console.log('[OrderStatusWidget] API Base URL:', apiBaseUrl, 'Shop:', shop);
  console.log('[OrderStatusWidget] Window location:', window.location.href);
  console.log('[OrderStatusWidget] Window origin:', window.location.origin);
  console.log('[OrderStatusWidget] Is in iframe:', window.self !== window.top);

  // Load settings and FAQs on mount
  useEffect(() => {
    if (shop) {
      // First, test if fetch is available
      console.log('[OrderStatusWidget] Fetch available:', typeof fetch !== 'undefined');
      console.log('[OrderStatusWidget] AbortController available:', typeof AbortController !== 'undefined');
      
      // Test basic fetch functionality with XMLHttpRequest as fallback
      console.log('[OrderStatusWidget] Testing basic fetch...');
      try {
        const testFetch = fetch('https://httpbin.org/get');
        console.log('[OrderStatusWidget] External fetch test created');
        testFetch
          .then(res => {
            console.log('[OrderStatusWidget] ✅ External fetch works! Status:', res.status);
            return res.json();
          })
          .then(data => {
            console.log('[OrderStatusWidget] ✅ External fetch completed');
          })
          .catch(err => {
            console.error('[OrderStatusWidget] ❌ External fetch failed:', err);
            console.error('[OrderStatusWidget] Error details:', err.name, err.message);
          });
      } catch (error) {
        console.error('[OrderStatusWidget] ❌ Failed to create external fetch:', error);
      }
      
      // Also test with XMLHttpRequest to see if it's a fetch-specific issue
      console.log('[OrderStatusWidget] Testing XMLHttpRequest...');
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${apiBaseUrl}/api/test`, true);
        xhr.onload = function() {
          console.log('[OrderStatusWidget] ✅ XMLHttpRequest works! Status:', xhr.status);
          console.log('[OrderStatusWidget] XMLHttpRequest response:', xhr.responseText);
        };
        xhr.onerror = function() {
          console.error('[OrderStatusWidget] ❌ XMLHttpRequest failed');
        };
        xhr.ontimeout = function() {
          console.error('[OrderStatusWidget] ❌ XMLHttpRequest timed out');
        };
        xhr.timeout = 10000;
        xhr.send();
        console.log('[OrderStatusWidget] XMLHttpRequest sent');
      } catch (error) {
        console.error('[OrderStatusWidget] ❌ Failed to create XMLHttpRequest:', error);
      }
      
      // First, test connectivity with a simple endpoint
      console.log('[OrderStatusWidget] Testing server connectivity...');
      console.log('[OrderStatusWidget] Test URL:', `${apiBaseUrl}/api/test`);
      console.log('[OrderStatusWidget] Current location:', window.location.href);
      console.log('[OrderStatusWidget] Is in iframe:', window.self !== window.top);
      
      const testUrl = `${apiBaseUrl}/api/test`;
      console.log('[OrderStatusWidget] About to fetch:', testUrl);
      
      try {
        const testFetch = fetch(testUrl);
        console.log('[OrderStatusWidget] Fetch promise created');
        testFetch
          .then(res => {
            console.log('[OrderStatusWidget] Test response received:', res.status, res.statusText);
            return res.json();
          })
          .then(data => {
            console.log('[OrderStatusWidget] ✅ Server connectivity test passed:', data);
          })
          .catch(error => {
            console.error('[OrderStatusWidget] ❌ Server connectivity test failed:', error);
            console.error('[OrderStatusWidget] Error name:', error.name);
            console.error('[OrderStatusWidget] Error message:', error.message);
            console.error('[OrderStatusWidget] Error stack:', error.stack);
          });
      } catch (error) {
        console.error('[OrderStatusWidget] ❌ Failed to create fetch request:', error);
      }
      
      // Use public endpoints for customer-facing widget (no auth required)
      console.log('[OrderStatusWidget] Loading settings from:', `${apiBaseUrl}/api/admin/settings/public?shop=${encodeURIComponent(shop)}`);
      
      // Add timeout to fetch
      const settingsController = new AbortController();
      const settingsTimeout = setTimeout(() => {
        console.error('[OrderStatusWidget] Settings request timed out after 10 seconds');
        settingsController.abort();
      }, 10000);
      
      const settingsUrl = `${apiBaseUrl}/api/admin/settings/public?shop=${encodeURIComponent(shop)}`;
      console.log('[OrderStatusWidget] About to fetch settings:', settingsUrl);
      
      // Use XMLHttpRequest since fetch is timing out
      console.log('[OrderStatusWidget] Using XMLHttpRequest for settings (fetch has issues)');
      const settingsXhr = new XMLHttpRequest();
      settingsXhr.open('GET', settingsUrl, true);
      settingsXhr.timeout = 10000;
      settingsXhr.setRequestHeader('Accept', 'application/json');
      
      const settingsPromise = new Promise<Response>((resolve, reject) => {
        settingsXhr.onload = function() {
          clearTimeout(settingsTimeout);
          const response = new Response(settingsXhr.responseText, {
            status: settingsXhr.status,
            statusText: settingsXhr.statusText,
          });
          resolve(response);
        };
        settingsXhr.onerror = function() {
          clearTimeout(settingsTimeout);
          reject(new Error('Network error'));
        };
        settingsXhr.ontimeout = function() {
          clearTimeout(settingsTimeout);
          reject(new Error('Request timeout'));
        };
      });
      
      settingsXhr.send();
      console.log('[OrderStatusWidget] Settings XMLHttpRequest sent');
      
      settingsPromise
        .then((res) => {
          clearTimeout(settingsTimeout);
          console.log('[OrderStatusWidget] Settings response status:', res.status, res.statusText);
          if (!res.ok) {
            console.error('[OrderStatusWidget] Failed to load settings:', res.status, res.statusText);
            return res.text().then(text => {
              console.error('[OrderStatusWidget] Settings error details:', text);
              console.warn('[OrderStatusWidget] Continuing with default settings');
              return null;
            });
          }
          return res.json();
        })
        .then((data) => {
          if (data) {
            console.log('[OrderStatusWidget] Settings loaded successfully');
            setSettings(data);
          } else {
            console.warn('[OrderStatusWidget] No settings data, using defaults');
            // Keep settings as null to use defaults
          }
        })
        .catch((error) => {
          clearTimeout(settingsTimeout);
          if (error.name === 'AbortError') {
            console.error('[OrderStatusWidget] Settings request was aborted (timeout or cancelled)');
          } else {
            console.error('[OrderStatusWidget] Error loading settings:', error);
            console.error('[OrderStatusWidget] Error name:', error.name);
            console.error('[OrderStatusWidget] Error message:', error.message);
            console.error('[OrderStatusWidget] Error stack:', error.stack);
          }
          console.warn('[OrderStatusWidget] Continuing with default settings');
          // Keep settings as null to use defaults
        });

      const faqUrl = `${apiBaseUrl}/api/faq/public?shop=${encodeURIComponent(shop)}`;
      console.log('[OrderStatusWidget] Loading FAQs from:', faqUrl);
      
      // Use XMLHttpRequest since fetch is timing out
      console.log('[OrderStatusWidget] Using XMLHttpRequest for FAQs (fetch has issues)');
      const faqXhr = new XMLHttpRequest();
      faqXhr.open('GET', faqUrl, true);
      faqXhr.timeout = 10000;
      faqXhr.setRequestHeader('Accept', 'application/json');
      
      const faqTimeout = setTimeout(() => {
        console.error('[OrderStatusWidget] FAQs request timed out after 10 seconds');
        faqXhr.abort();
      }, 10000);
      
      const faqPromise = new Promise<Response>((resolve, reject) => {
        faqXhr.onload = function() {
          clearTimeout(faqTimeout);
          const response = new Response(faqXhr.responseText, {
            status: faqXhr.status,
            statusText: faqXhr.statusText,
          });
          resolve(response);
        };
        faqXhr.onerror = function() {
          clearTimeout(faqTimeout);
          reject(new Error('Network error'));
        };
        faqXhr.ontimeout = function() {
          clearTimeout(faqTimeout);
          reject(new Error('Request timeout'));
        };
      });
      
      faqXhr.send();
      console.log('[OrderStatusWidget] FAQ XMLHttpRequest sent');
      
      faqPromise
        .then((res) => {
          console.log('[OrderStatusWidget] FAQs response status:', res.status, res.statusText);
          if (!res.ok) {
            console.error('[OrderStatusWidget] Failed to load FAQs:', res.status, res.statusText);
            return res.text().then(text => {
              console.error('[OrderStatusWidget] FAQs error details:', text);
              return [];
            });
          }
          return res.json();
        })
        .then((data) => {
          if (Array.isArray(data)) {
            console.log(`[OrderStatusWidget] Loaded ${data.length} FAQs`);
            setFaqs(data);
          } else {
            console.warn('[OrderStatusWidget] FAQs data is not an array');
            setFaqs([]);
          }
        })
        .catch((error) => {
          if (error.message === 'Request timeout' || error.message === 'Network error') {
            console.error('[OrderStatusWidget] FAQs request failed:', error.message);
          } else {
            console.error('[OrderStatusWidget] Error loading FAQs:', error);
            console.error('[OrderStatusWidget] Error name:', error.name);
            console.error('[OrderStatusWidget] Error message:', error.message);
            console.error('[OrderStatusWidget] Error stack:', error.stack);
          }
          setFaqs([]); // Set empty array on error
        });
    }
  }, [shop, apiBaseUrl]);

  const handleSubmit = async () => {
    // Client-side validation
    if (!orderNumber.trim()) {
      setError('Order number is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusData(null);

    try {
      console.log('[OrderStatusWidget] Submitting order status request:', { shop, orderNumber: orderNumber.trim(), apiBaseUrl });
      
      // Use XMLHttpRequest since fetch is timing out
      const orderStatusUrl = `${apiBaseUrl}/api/public/order-status`;
      console.log('[OrderStatusWidget] Using XMLHttpRequest for order status (fetch has issues)');
      
      const response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', orderStatusUrl, true);
        xhr.timeout = 15000;
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        
        const timeout = setTimeout(() => {
          console.error('[OrderStatusWidget] Order status request timed out after 15 seconds');
          xhr.abort();
          reject(new Error('Request timeout'));
        }, 15000);
        
        xhr.onload = function() {
          clearTimeout(timeout);
          const response = new Response(xhr.responseText, {
            status: xhr.status,
            statusText: xhr.statusText,
          });
          resolve(response);
        };
        
        xhr.onerror = function() {
          clearTimeout(timeout);
          reject(new Error('Network error'));
        };
        
        xhr.ontimeout = function() {
          clearTimeout(timeout);
          reject(new Error('Request timeout'));
        };
        
        xhr.send(JSON.stringify({
          shop,
          orderNumber: orderNumber.trim(),
          email: email.trim(),
        }));
      });

      console.log('[OrderStatusWidget] Order status response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[OrderStatusWidget] Order status API error:', response.status, errorText);
        setError(`Failed to fetch order status (${response.status}): ${errorText || response.statusText}`);
        setLoading(false);
        return;
      }

      const data: OrderStatusResponse = await response.json();
      console.log('[OrderStatusWidget] Order status response data:', data);

      if (data.ok) {
        setStatusData(data);
        setError(null);
      } else {
        setError(data.message || 'An error occurred');
        setStatusData(null);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.error('[OrderStatusWidget] Order status request was aborted (timeout)');
        setError('Request timed out. Please check your connection and try again.');
      } else {
        console.error('[OrderStatusWidget] Order status error:', err);
        console.error('[OrderStatusWidget] Error name:', err.name);
        console.error('[OrderStatusWidget] Error message:', err.message);
        setError(`Failed to fetch order status: ${err.message || 'Network error'}. Please try again.`);
      }
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Default to enabled if settings haven't loaded yet or are explicitly enabled
  // This ensures the widget is visible even if settings fail to load
  const isFaqEnabled = settings === null ? true : (settings?.faq_enabled === 1 || settings?.faq_enabled === true);
  const isOrderStatusEnabled = settings === null ? true : (settings?.order_status_enabled === 1 || settings?.order_status_enabled === true);

  return (
    <div style={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
      <BlockStack gap="500" style={{ backgroundColor: 'white', width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
      {/* FAQ Section - Now at the top */}
      {isFaqEnabled && (
        <div style={{ 
          width: '100%',
          maxWidth: '100%',
          display: 'block', 
          marginBottom: '32px',
          border: `2px solid ${settings?.faq_border_color || '#e1e3e5'}`,
          borderRadius: '8px',
          backgroundColor: settings?.faq_bg_color || '#fafbfb',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <BlockStack gap="200">
            <Text variant="headingLg" as="h2" fontWeight="bold" style={{ color: settings?.faq_text_color || '#202223', marginBottom: '8px' }}>
              {settings?.faq_section_title || 'Frequently Asked Questions'}
            </Text>
            {settings?.faq_section_subtitle && (
              <Text as="p" color="subdued" style={{ color: settings?.faq_text_color || '#202223', opacity: 0.7, marginBottom: '12px' }}>
                {settings.faq_section_subtitle}
              </Text>
            )}
            {faqs.length === 0 ? (
              <Text as="p" color="subdued" style={{ color: settings?.faq_text_color || '#6d7175', padding: '12px 0' }}>
                No FAQs available at this time.
              </Text>
            ) : (
              faqs.map((faq) => (
              <div key={faq.id} style={{ borderBottom: `1px solid ${settings?.faq_border_color || '#e1e3e5'}`, paddingBottom: '4px', marginBottom: '4px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    cursor: 'pointer',
                    padding: '6px 0',
                    gap: '12px',
                  }}
                  onClick={() => setFaqOpen(faqOpen === faq.id ? null : faq.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setFaqOpen(faqOpen === faq.id ? null : faq.id);
                    }
                  }}
                  aria-expanded={faqOpen === faq.id}
                  aria-controls={`faq-answer-${faq.id}`}
                >
                  <Text 
                    variant="bodyLg" 
                    as="span" 
                    fontWeight="semibold"
                    style={{
                      flex: '1',
                      minWidth: 0,
                      wordBreak: 'break-word',
                      paddingRight: '8px',
                    }}
                  >
                    {faq.question}
                  </Text>
                  <div
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      marginLeft: '8px',
                    }}
                  >
                    <Icon 
                      source={faqOpen === faq.id ? ChevronUpMinor : ChevronDownMinor}
                      tone="subdued"
                    />
                  </div>
                </div>
                <Collapsible
                  open={faqOpen === faq.id}
                  id={`faq-answer-${faq.id}`}
                  transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                  expandOnPrint
                >
                  <div style={{ padding: '0 0 4px 0', color: settings?.faq_text_color || '#202223', opacity: 0.8 }}>
                    <Text as="p" color="subdued" variant="bodyMd">
                      {faq.answer.split('\n').map((line, i) => (
                        <span key={i} style={{ color: settings?.faq_text_color || '#6d7175' }}>
                          {line.includes('http') || line.includes('@') ? (
                            <a
                              href={line.includes('http') ? line : `mailto:${line}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: settings?.button_color || '#007ace', textDecoration: 'underline' }}
                            >
                              {line}
                            </a>
                          ) : (
                            line
                          )}
                          <br />
                        </span>
                      ))}
                    </Text>
                  </div>
                </Collapsible>
              </div>
            ))
            )}
          </BlockStack>
        </div>
      )}

      {/* Order Status Form */}
      {isOrderStatusEnabled && (
      <div style={{ 
        width: '100%',
        maxWidth: '100%',
        display: 'block',
        border: `2px solid ${settings?.section_border_color || '#e1e3e5'}`,
        borderRadius: '8px',
        backgroundColor: settings?.section_bg_color || '#fafbfb',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxHeight: '800px',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        <div>
        <BlockStack gap="400">
          <Text variant="headingLg" as="h2" fontWeight="bold" style={{ color: settings?.section_text_color || '#202223', marginBottom: '8px' }}>
            {settings?.section_title || 'Order Status'}
          </Text>
          {settings?.section_subtitle && (
            <Text as="p" color="subdued" style={{ color: settings?.section_text_color || '#202223', opacity: 0.7, marginTop: '-8px', marginBottom: '8px' }}>
              {settings.section_subtitle}
            </Text>
          )}
          {settings?.helper_text && (
            <Text as="p" color="subdued" style={{ color: settings?.section_text_color || '#6d7175' }}>
              {settings.helper_text}
            </Text>
          )}

          {error && (
            <Banner status="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          <TextField
            label="Order Number"
            value={orderNumber}
            onChange={setOrderNumber}
            placeholder="e.g., #1234"
            disabled={loading}
            autoComplete="off"
          />

          <TextField
            label="Email Address"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="customer@example.com"
            disabled={loading}
            autoComplete="email"
            helpText="Enter the email address used when placing the order"
          />

          <Button
            primary
            onClick={handleSubmit}
            loading={loading}
            disabled={!orderNumber.trim() || !email.trim()}
            style={{
              backgroundColor: settings?.button_color || '#008060',
              borderColor: settings?.button_color || '#008060',
            }}
          >
            {settings?.button_label || 'Check Status'}
          </Button>

          {settings?.disclaimer_text && (
            <Text as="p" variant="bodySm" color="subdued" style={{ color: settings?.section_text_color || '#6d7175' }}>
              {settings.disclaimer_text}
            </Text>
          )}
        </BlockStack>

        {statusData?.ok && (
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '24px' }}>
          {/* Left Column: Order Status */}
          <div style={{ flex: '1 1 500px', minWidth: '300px' }}>
            <Card>
              <BlockStack gap="500">
                {/* Header Section */}
                <BlockStack gap="200">
                  <Text variant="bodySm" color="subdued" fontWeight="semibold">
                    {statusData.currentStatus?.label?.toUpperCase() || 'ORDER STATUS'}
                  </Text>
                  <Text variant="heading2xl" as="h2" fontWeight="bold">
                    Order {statusData.orderNumber}
                  </Text>
                  <Text as="p" color="subdued">
                    Please come back to this page for more status updates!
                  </Text>
                </BlockStack>

            {/* Timeline Section */}
            {statusData.timeline && statusData.timeline.length > 0 && (
              <div style={{ position: 'relative', paddingLeft: '24px', marginTop: '24px' }}>
                <BlockStack gap="500">
                  {statusData.timeline.map((milestone, index) => {
                    const orderDate = new Date(statusData.createdAt || '');
                    const now = new Date();
                    
                    // Calculate which stages should be completed based on days since order
                    const daysSinceOrder = Math.floor(
                      (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    const shouldBeCompleted = daysSinceOrder >= milestone.offsetDays;
                    
                    // Check if previous stage is completed (for line coloring)
                    const prevCompleted = index === 0 || 
                      (index > 0 && daysSinceOrder >= statusData.timeline[index - 1].offsetDays);

                    return (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '16px',
                        }}
                      >
                        {/* Vertical line segment (only show if not last item) */}
                        {index < statusData.timeline.length - 1 && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '11px',
                              top: '24px',
                              width: '2px',
                              height: '64px',
                              backgroundColor: prevCompleted && shouldBeCompleted ? '#008060' : '#e1e3e5',
                              zIndex: 0,
                            }}
                          />
                        )}

                        {/* Circle indicator */}
                        <div
                          style={{
                            position: 'relative',
                            zIndex: 1,
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: shouldBeCompleted ? '#008060' : '#ffffff',
                            border: shouldBeCompleted ? 'none' : '2px solid #bfc1c3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: '2px',
                          }}
                        >
                          {shouldBeCompleted && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M11.6667 3.5L5.25 9.91667L2.33334 7"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, paddingBottom: index < statusData.timeline.length - 1 ? '32px' : '0' }}>
                          <Text
                            as="p"
                            variant="bodyMd"
                            fontWeight="bold"
                            style={{
                              color: shouldBeCompleted ? '#008060' : '#202223',
                              letterSpacing: '0.5px',
                              marginBottom: '4px',
                              display: 'block',
                            }}
                          >
                            {milestone.label.toUpperCase()}
                          </Text>
                          {shouldBeCompleted && (
                            <Text 
                              as="p" 
                              variant="bodySm" 
                              style={{
                                color: '#6d7175',
                                fontSize: '13px',
                                marginTop: '0',
                                display: 'block',
                              }}
                            >
                              {formatDateShort(milestone.date)}
                            </Text>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </BlockStack>
              </div>
            )}

                {/* Search Again Button */}
                <div style={{ marginTop: '24px' }}>
                  <Button
                    size="large"
                    fullWidth
                    onClick={() => {
                      setStatusData(null);
                      setOrderNumber('');
                      setEmail('');
                      setError(null);
                    }}
                  >
                    Search Again
                  </Button>
                </div>
              </BlockStack>
            </Card>
          </div>

          {/* Right Column: Order Details */}
          {statusData.orderDetails && (
            <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
              <Card>
                <BlockStack gap="500">
                  <Text variant="headingMd" as="h3" fontWeight="bold">
                    Order Summary
                  </Text>

                  {/* Product Items */}
                  <BlockStack gap="400">
                    {statusData.orderDetails.lineItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          paddingBottom: '16px',
                          borderBottom: '1px solid #e1e3e5',
                          gap: '4px',
                        }}
                      >
                        {/* Product Info */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text as="p" variant="bodyMd" fontWeight="medium">
                              {item.title}
                            </Text>
                            {item.quantity > 1 && (
                              <Text as="p" variant="bodySm" color="subdued">
                                x{item.quantity}
                              </Text>
                            )}
                          </div>
                          {item.variantTitle && (
                            <Text as="p" variant="bodySm" color="subdued">
                              {item.variantTitle}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </BlockStack>

                </BlockStack>
              </Card>
            </div>
          )}
        </div>
        )}
        </div>
      </div>
      )}
      </BlockStack>
    </div>
  );
}

